import React from "react";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
} from "react-router-dom";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
import { styled } from "@mui/material/styles";
import { _get_is_seen_video_localStorage } from "src/DAL/localStorage/localStorage";
import ReactGA from "react-ga";
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { memberDataQuery } from "src/Hooks/memberDataQuery";
import { useSnackbar } from "notistack";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { ContentSettingApi } from "src/DAL/ContentSetting/ContentSetting";
import { gr } from "src/design-system/tokens";
import GrCountBadge from "src/design-system/components/GrCountBadge";

const ListItemStyle = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  // GymRite Design Elements: sidebar item (44px, inset 12px, radius 12, ink-2 text).
  height: 44,
  margin: `2px ${gr.space[3]}`,
  width: `calc(100% - 2 * ${gr.space[3]})`,
  borderRadius: gr.radius.tile,
  gap: gr.space[3],
  position: "relative",
  textTransform: "capitalize",
  paddingLeft: gr.space[4],
  paddingRight: gr.space[3],
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  fontWeight: gr.font.weight.regular,
  color: gr.color.ink2,
  "&:before": {
    top: 10,
    left: 0,
    width: 3,
    bottom: 10,
    content: "''",
    display: "none",
    position: "absolute",
    borderRadius: `0 ${gr.radius.pill} ${gr.radius.pill} 0`,
    backgroundColor: gr.color.brand,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 20,
  height: 20,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: gr.color.ink2,
  "& svg": { width: 20, height: 20 },
});

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

function NavItem({ item, active }) {
  const isActiveRoot = active(item);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const activeRootStyle = {
    color: gr.color.brandDark,
    fontWeight: gr.font.weight.semibold,
    bgcolor: gr.color.brandSoft,
    "&:before": { display: "block" },
  };
  const activeRootStyles = {
    color: gr.color.brandStrong,
  };

  const activeSubStyle = {
    color: "text.primary",
    fontWeight: "fontWeightMedium",
  };
  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle className="sidebar-icon">
            {icon && icon}
          </ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Box
            component={Icon}
            icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle),
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: "flex",
                        borderRadius: "50%",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "text.disabled",
                        transition: (theme) =>
                          theme.transitions.create("transform"),
                        ...(isActiveSub && {
                          transform: "scale(2)",
                          bgcolor: "primary.main",
                        }),
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle
        sx={{
          ...(isActiveRoot && activeRootStyles),
        }}
      >
        {icon && icon}
      </ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      <GrCountBadge count={info} tone={isActiveRoot ? "solid" : "brand"} />
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, ...other }) {
  const location = useLocation();
  const { pathname } = useLocation();
  const is_seen_video = _get_is_seen_video_localStorage();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const {
    handleContentSettingData,
    handleDashboardSettingData,
    handleDashboardTestimonialData,
    handleDashboardEventData,
    handleAdminTimeZone,
    setUserInfo,
    dashboardSettingData,
  } = useContentSetting();

  const getContentSetting = async () => {
    const result = await ContentSettingApi();
    if (result.code === 200) {
      if (result.content_setting === null) {
        handleDashboardSettingData({});
        handleContentSettingData({});
        handleDashboardTestimonialData([]);
        handleDashboardEventData([]);
        handleAdminTimeZone("");
        return;
      }
      localStorage.setItem(
        `content_setting`,
        JSON.stringify(result?.content_setting?.client_content_setting)
      );
      localStorage.setItem(
        `client_dashboard_setting`,
        JSON.stringify(result?.content_setting?.client_dashboard_setting)
      );
      localStorage.setItem(`testmonial`, JSON.stringify(result?.testmonial));
      localStorage.setItem(`home_event`, JSON.stringify(result?.home_event));
      localStorage.setItem(
        `admin_time_zone`,
        JSON.stringify(result?.time_zone)
      );
      handleAdminTimeZone(result?.time_zone);
      handleDashboardEventData(result?.home_event);
      handleDashboardTestimonialData(result?.testmonial);
      handleContentSettingData(result?.content_setting.client_content_setting);
      handleDashboardSettingData(
        result?.content_setting?.client_dashboard_setting
      );
      if (
        dashboardSettingData?.video_url !==
          result.content_setting.client_dashboard_setting?.video_url ||
        result.content_setting.client_dashboard_setting?.video_type == "repeat"
      ) {
        localStorage.setItem("is_seen_video", "No");
      }
      ReactGA.initialize(
        result.content_setting.client_general_setting.analytic_google_code,
        { debug: true }
      );
      // The profile through the shared React Query entry (the dashboard reuses it instead of asking
      // again). The pending-forms count is loaded by AccountPopover on every page, so it is kept
      // here, not fetched a second time.
      let member;
      try {
        member = (await queryClient.fetchQuery(memberDataQuery())).member;
      } catch (error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
      if (member) {
        localStorage.setItem(`user_data`, JSON.stringify(member));
        localStorage.setItem(`user_data_new`, JSON.stringify(member));
        setUserInfo((values) => ({ ...values, ...member }));
        if (is_seen_video && is_seen_video === "Yes") {
        } else {
          localStorage.setItem("is_seen_video", "No");
        }
      }
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  useEffect(() => {
    getContentSetting();
  }, []);

  useEffect(() => {
    ReactGA.pageview(location.pathname + location.search);
  }, [location]);

  const match = ({ path, matches }) => {
    if (matches) {
      let is_active = false;
      matches.forEach((match_path) => {
        const match = match_path
          ? !!matchPath({ path: match_path, end: false }, pathname)
          : false;

        if (match) {
          is_active = true;
        }
      });
      return is_active;
    }
    return path ? !!matchPath({ path, end: false }, pathname) : false;
  };

  return (
    <Box {...other}>
      <List disablePadding>
        {navConfig.map((item, i) => {
          return <NavItem key={item.title} item={item} active={match} />;
        })}
      </List>
    </Box>
  );
}
